import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class CoreInputTag implements Plugin {
  private input: HTMLInputElement | null;
  private tagsContainer: HTMLElement | null;
  private keydownHandler: (e: KeyboardEvent) => void;
  private clickHandler: (e: MouseEvent) => void;
  private selectHandler: (e: Event) => void;

  constructor(private readonly element: HTMLElement) {
    this.input = this.element.querySelector(".core-inputTag__input");
    this.tagsContainer = this.element.querySelector(".core-inputTag__tags");

    this.keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
    this.clickHandler = (e: MouseEvent) => this.handleClick(e);
    this.selectHandler = (e: Event) => this.handleSelect(e as CustomEvent);
  }

  init(): void {
    if (this.input) {
      this.input.addEventListener("keydown", this.keydownHandler);
    }
    if (this.tagsContainer) {
      this.tagsContainer.addEventListener("click", this.clickHandler);
    }
    this.element.addEventListener("coreInputTag:select", this.selectHandler);
    this.updateState();
  }

  private handleSelect(event: CustomEvent<{ value: string }>): void {
    const value = event.detail?.value?.trim();
    if (!value) return;
    this.addTag(value);
    if (this.input) {
      this.input.value = "";
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      if (this.element.hasAttribute("data-options")) return;
      const value = this.input?.value.trim() || "";
      if (value) {
        this.addTag(value);
        if (this.input) this.input.value = "";
      }
    } else if (event.key === "Backspace" && this.input?.value === "") {
      const tags = this.tagsContainer?.querySelectorAll(".core-tagInput");
      if (tags && tags.length > 0) {
        const lastTag = tags[tags.length - 1] as HTMLElement;
        const value = lastTag.dataset.value || "";
        this.removeTag(lastTag, value);
      }
    }
  }

  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const tag = target.closest(".core-tagInput") as HTMLElement;
    if (tag) {
      const value = tag.dataset.value || "";
      this.removeTag(tag, value);
    }
  }

  private addTag(value: string): void {
    if (!this.tagsContainer) return;

    const existingTags = this.tagsContainer.querySelectorAll(
      `[data-value="${value}"]`,
    );
    if (existingTags.length > 0) return;

    const size = this.element.classList.contains("core-inputTag--lg")
      ? "lg"
      : this.element.classList.contains("core-inputTag--base")
        ? "base"
        : "sm";

    const tagVariant = this.element.dataset.tagVariant;
    const variantClass = tagVariant ? ` core-tagInput--${tagVariant}` : "";

    const tag = document.createElement("div");
    tag.className = `core-tagInput core-tagInput--${size}${variantClass}`;
    tag.dataset.value = value;
    tag.setAttribute("role", "listitem");

    tag.innerHTML = `
      <span class="core-tagInput__label">${value}</span>
      <button
        type="button"
        class="core-tagInput__remove"
        aria-label="Eliminar ${value}"
        data-action="remove"
      >
        <span class="icon__svg--3 core-tagInput__icon dsc-co-icon-clean" aria-hidden="true"></span>
      </button>
    `;

    if (this.input) {
      this.tagsContainer.insertBefore(tag, this.input);
    } else {
      this.tagsContainer.appendChild(tag);
    }

    this.element.dispatchEvent(
      new CustomEvent("addTag", {
        bubbles: true,
        detail: { value },
      }),
    );

    this.updateState();
  }

  private removeTag(tagElement: HTMLElement, value: string): void {
    tagElement.remove();

    this.element.dispatchEvent(
      new CustomEvent("removeTag", {
        bubbles: true,
        detail: { value },
      }),
    );

    this.updateState();
  }

  private updateState(): void {
    const hasTags =
      this.tagsContainer?.querySelectorAll(".core-tagInput").length || 0;
    this.element.classList.toggle("core-inputTag--filled", hasTags > 0);
  }

  destroy(): void {
    if (this.input) {
      this.input.removeEventListener("keydown", this.keydownHandler);
    }
    if (this.tagsContainer) {
      this.tagsContainer.removeEventListener("click", this.clickHandler);
    }
    this.element.removeEventListener("coreInputTag:select", this.selectHandler);
  }
}

PluginRegistry.register("coreInputTag", CoreInputTag);
